import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useRef, useState, type FormEvent } from "react";
import { supabase } from "@/integrations/supabase/client";
import { submitToIndexNow } from "@/lib/indexnow.functions";
import { submitSitemapToGSC } from "@/lib/gsc.functions";

export const Route = createFileRoute("/admin/news")({
  component: AdminNews,
  ssr: false,
  head: () => ({
    meta: [
      { title: "Admin — Comm42 SA" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
});

type Article = {
  id: string;
  slug: string;
  title: string;
  excerpt: string | null;
  content: string;
  cover_image_url: string | null;
  language: string;
  published: boolean;
  published_at: string | null;
};

const empty: Partial<Article> = {
  slug: "",
  title: "",
  excerpt: "",
  content: "",
  cover_image_url: "",
  language: "it",
  published: false,
};

function slugify(s: string) {
  return s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "").slice(0, 80);
}

function AdminNews() {
  const contentRef = useRef<HTMLTextAreaElement | null>(null);
  const [ready, setReady] = useState(false);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [articles, setArticles] = useState<Article[]>([]);
  const [loadingList, setLoadingList] = useState(false);
  const [editing, setEditing] = useState<Partial<Article> | null>(null);
  const [nextImageAlt, setNextImageAlt] = useState("");

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setUserEmail(data.session?.user?.email ?? null);
      setReady(true);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_e, s) => {
      setUserEmail(s?.user?.email ?? null);
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  const loadList = async () => {
    setLoadingList(true);
    setMsg(null);
    try {
      const { data, error } = await supabase
        .from("news_articles")
        .select("*")
        .order("created_at", { ascending: false });
      if (error) throw error;
      setArticles(data as Article[]);
    } catch (e) {
      setMsg("Errore: " + (e as Error).message);
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => {
    if (userEmail) void loadList();
    else setArticles([]);
  }, [userEmail]);

  const login = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setMsg(null);
    try {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
    } catch (err) {
      setMsg("Login fallito: " + (err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const logout = async () => {
    await supabase.auth.signOut();
    setUserEmail(null);
  };

  const save = async (a: Partial<Article>) => {
    setBusy(true);
    setMsg(null);
    try {
      const slug = a.slug || slugify(a.title || "");
      const payload = {
        slug,
        title: a.title || "",
        excerpt: a.excerpt || null,
        content: a.content || "",
        cover_image_url: a.cover_image_url || null,
        language: a.language || "it",
        published: !!a.published,
        published_at: a.published ? (a.published_at || new Date().toISOString()) : null,
      };
      if (a.id) {
        const { error } = await supabase.from("news_articles").update(payload).eq("id", a.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("news_articles").insert(payload);
        if (error) throw error;
      }
      // Ping IndexNow (Bing/Yandex/etc.) when an article is published.
      // Google: rely on Search Console — they dropped the ping endpoint in 2023.
      if (payload.published) {
        try {
          await submitToIndexNow({
            data: {
              urls: [
                `https://comm42.ch/news/${payload.slug}`,
                `https://comm42.ch/news`,
                `https://comm42.ch/sitemap.xml`,
              ],
            },
          });
        } catch (err) {
          console.warn("IndexNow ping failed (non-blocking):", err);
        }
        try {
          await submitSitemapToGSC({ data: { sitemapPath: "sitemap.xml" } });
        } catch (err) {
          console.warn("Google Search Console sitemap submit failed (non-blocking):", err);
        }
      }
      setEditing(null);
      await loadList();
    } catch (err) {
      setMsg("Salvataggio fallito: " + (err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id: string) => {
    if (!confirm("Eliminare l'articolo?")) return;
    setBusy(true);
    try {
      const { error } = await supabase.from("news_articles").delete().eq("id", id);
      if (error) throw error;
      await loadList();
    } catch (err) {
      setMsg("Eliminazione fallita: " + (err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const uploadImage = async (file: File): Promise<string | null> => {
    setBusy(true);
    setMsg(null);
    try {
      const ext = file.name.split(".").pop()?.toLowerCase() || "bin";
      const path = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
      const { error } = await supabase.storage.from("news-images").upload(path, file, {
        cacheControl: "3600", upsert: false, contentType: file.type,
      });
      if (error) throw error;
      const { data } = supabase.storage.from("news-images").getPublicUrl(path);
      return data.publicUrl;
    } catch (err) {
      setMsg("Upload fallito: " + (err as Error).message);
      return null;
    } finally {
      setBusy(false);
    }
  };

  const insertContent = (insert: string | ((selected: string) => string), fallbackSelection = "") => {
    if (!editing) return;
    const current = editing?.content || "";
    const textarea = contentRef.current;
    const start = textarea?.selectionStart ?? current.length;
    const end = textarea?.selectionEnd ?? current.length;
    const selected = current.slice(start, end) || fallbackSelection;
    const value = typeof insert === "function" ? insert(selected) : insert;
    const next = current.slice(0, start) + value + current.slice(end);
    const cursor = start + value.length;

    setEditing({ ...editing, content: next });
    window.requestAnimationFrame(() => {
      contentRef.current?.focus();
      contentRef.current?.setSelectionRange(cursor, cursor);
    });
  };

  const selectedContentText = () => {
    const current = editing?.content || "";
    const textarea = contentRef.current;
    if (!textarea || textarea.selectionStart === textarea.selectionEnd) return "";
    return current.slice(textarea.selectionStart, textarea.selectionEnd);
  };

  if (!ready) {
    return <div style={{ padding: 40, fontFamily: "system-ui" }}>Caricamento…</div>;
  }

  if (!userEmail) {
    return (
      <div style={{ maxWidth: 400, margin: "80px auto", padding: 24, fontFamily: "system-ui" }}>
        <h1 style={{ fontSize: 28, marginBottom: 8 }}>Admin News — Login</h1>
        <p style={{ color: "#666", marginBottom: 24 }}>Accedi per gestire gli articoli.</p>
        <form onSubmit={login} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <input type="email" placeholder="Email" required value={email}
            onChange={(e) => setEmail(e.target.value)}
            style={{ padding: 10, border: "1px solid #ccc", borderRadius: 6, fontSize: 14 }} />
          <input type="password" placeholder="Password" required value={password}
            onChange={(e) => setPassword(e.target.value)}
            style={{ padding: 10, border: "1px solid #ccc", borderRadius: 6, fontSize: 14 }} />
          <button type="submit" disabled={busy}
            style={{ padding: 12, background: "#2c6e8c", color: "#fff", border: 0, borderRadius: 6, fontSize: 14, cursor: "pointer" }}>
            {busy ? "…" : "Accedi"}
          </button>
        </form>
        {msg && <p style={{ marginTop: 16, color: "#c00", fontSize: 13 }}>{msg}</p>}
      </div>
    );
  }

  if (editing) {
    return (
      <div style={{ maxWidth: 800, margin: "40px auto", padding: 24, fontFamily: "system-ui" }}>
        <button onClick={() => setEditing(null)} style={{ marginBottom: 16, padding: "6px 12px", border: "1px solid #ccc", background: "#fff", borderRadius: 6, cursor: "pointer" }}>
          ← Indietro
        </button>
        <h1 style={{ fontSize: 24, marginBottom: 16 }}>{editing.id ? "Modifica articolo" : "Nuovo articolo"}</h1>
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <label style={{ fontSize: 12, color: "#666" }}>Titolo
            <input value={editing.title || ""} onChange={(e) => setEditing({ ...editing, title: e.target.value, slug: editing.slug || slugify(e.target.value) })}
              style={{ width: "100%", padding: 10, border: "1px solid #ccc", borderRadius: 6, fontSize: 14, marginTop: 4 }} />
          </label>
          <label style={{ fontSize: 12, color: "#666" }}>Slug
            <input value={editing.slug || ""} onChange={(e) => setEditing({ ...editing, slug: slugify(e.target.value) })}
              style={{ width: "100%", padding: 10, border: "1px solid #ccc", borderRadius: 6, fontSize: 14, marginTop: 4 }} />
          </label>
          <div style={{ display: "flex", gap: 12 }}>
            <label style={{ flex: 1, fontSize: 12, color: "#666" }}>Lingua
              <select value={editing.language || "it"} onChange={(e) => setEditing({ ...editing, language: e.target.value })}
                style={{ width: "100%", padding: 10, border: "1px solid #ccc", borderRadius: 6, fontSize: 14, marginTop: 4 }}>
                <option value="it">Italiano</option>
                <option value="en">English</option>
              </select>
            </label>
            <label style={{ flex: 1, display: "flex", alignItems: "flex-end", gap: 8, fontSize: 14, paddingBottom: 10 }}>
              <input type="checkbox" checked={!!editing.published} onChange={(e) => setEditing({ ...editing, published: e.target.checked })} />
              Pubblicato
            </label>
          </div>
          <label style={{ fontSize: 12, color: "#666" }}>URL immagine di copertina
            <input value={editing.cover_image_url || ""} onChange={(e) => setEditing({ ...editing, cover_image_url: e.target.value })}
              style={{ width: "100%", padding: 10, border: "1px solid #ccc", borderRadius: 6, fontSize: 14, marginTop: 4 }} />
          </label>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: -4 }}>
            <label style={{ fontSize: 13, padding: "8px 14px", background: "#f3f4f6", border: "1px solid #ccc", borderRadius: 6, cursor: "pointer" }}>
              📷 Carica copertina
              <input type="file" accept="image/*" style={{ display: "none" }}
                onChange={async (e) => {
                  const f = e.target.files?.[0]; if (!f) return;
                  const url = await uploadImage(f);
                  if (url) setEditing({ ...editing, cover_image_url: url });
                  e.target.value = "";
                }} />
            </label>
            {editing.cover_image_url && (
              <img src={editing.cover_image_url} alt="" style={{ height: 48, borderRadius: 4, border: "1px solid #eee" }} />
            )}
          </div>
          <label style={{ fontSize: 12, color: "#666" }}>Estratto
            <textarea rows={2} value={editing.excerpt || ""} onChange={(e) => setEditing({ ...editing, excerpt: e.target.value })}
              style={{ width: "100%", padding: 10, border: "1px solid #ccc", borderRadius: 6, fontSize: 14, marginTop: 4, fontFamily: "inherit" }} />
          </label>
          <label style={{ fontSize: 12, color: "#666" }}>Contenuto (Markdown supportato)
            <textarea ref={contentRef} rows={16} value={editing.content || ""} onChange={(e) => setEditing({ ...editing, content: e.target.value })}
              style={{ width: "100%", padding: 10, border: "1px solid #ccc", borderRadius: 6, fontSize: 14, marginTop: 4, fontFamily: "inherit" }} />
          </label>
          <div>
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 6 }}>
              <input
                type="text"
                value={nextImageAlt}
                onChange={(e) => setNextImageAlt(e.target.value)}
                placeholder="Alt text SEO della prossima immagine (es. Camion in autostrada)"
                style={{ flex: "1 1 280px", minWidth: 220, padding: "8px 10px", border: "1px solid #ccc", borderRadius: 6, fontSize: 13 }}
              />
              <label style={{ fontSize: 13, padding: "8px 14px", background: "#f3f4f6", border: "1px solid #ccc", borderRadius: 6, cursor: "pointer", display: "inline-block" }}
                title="L'immagine viene inserita nel punto in cui si trova il cursore. L'alt sopra viene salvato nel markdown.">
                🖼️ Inserisci immagine (al cursore)
                <input type="file" accept="image/*" style={{ display: "none" }}
                  onChange={async (e) => {
                    const f = e.target.files?.[0]; if (!f) return;
                    const alt = (nextImageAlt.trim() || f.name.replace(/\.[^.]+$/, "").replace(/[-_]+/g, " ")).trim();
                    const url = await uploadImage(f);
                    if (url) {
                      insertContent(`\n\n![${alt}](${url})\n\n`);
                      setNextImageAlt("");
                    }
                    e.target.value = "";
                  }} />
              </label>
            </div>
            <button type="button"
              onClick={() => {
                const selected = selectedContentText();
                const textarea = contentRef.current;
                const start = textarea?.selectionStart ?? (editing?.content?.length ?? 0);
                const end = textarea?.selectionEnd ?? start;
                const url = prompt("URL del link (es. https://esempio.ch):");
                if (!url) return;
                const current = editing?.content || "";
                if (selected) {
                  const value = `[${selected}](${url})`;
                  const next = current.slice(0, start) + value + current.slice(end);
                  setEditing({ ...editing, content: next });
                  const cursor = start + value.length;
                  window.requestAnimationFrame(() => {
                    contentRef.current?.focus();
                    contentRef.current?.setSelectionRange(cursor, cursor);
                  });
                } else {
                  const text = prompt("Testo da mostrare (lascia vuoto per usare l'URL):", "") || url;
                  insertContent(`[${text}](${url})`);
                }
              }}
              style={{ marginLeft: 8, padding: "8px 14px", background: "#f3f4f6", border: "1px solid #ccc", borderRadius: 6, cursor: "pointer", fontSize: 13 }}>
              🔗 Inserisci link (seleziona prima il testo)
            </button>
            <div style={{ marginTop: 8, fontSize: 12, color: "#666", lineHeight: 1.6 }}>
              <strong>Markdown supportato:</strong> <code>**grassetto**</code> · <code>*corsivo*</code> · <code>[testo](https://url)</code> · <code>![alt](url)</code> · <code>- elenco</code>
            </div>
            <div style={{ marginTop: 8, display: "flex", gap: 6, flexWrap: "wrap" }}>
              {([["H1","# "],["H2","## "],["H3","### "]] as const).map(([label, prefix]) => (
                <button key={label} type="button"
                  onClick={() => {
                    const txt = prompt(`Testo del ${label}:`);
                    if (!txt) return;
                    insertContent(`\n\n${prefix}${txt}\n\n`);
                  }}
                  style={{ padding: "6px 12px", background: "#f3f4f6", border: "1px solid #ccc", borderRadius: 6, cursor: "pointer", fontSize: 13, fontWeight: 600 }}>
                  {label}
                </button>
              ))}
              <button type="button"
                onClick={() => {
                  const selected = selectedContentText();
                  const txt = selected || prompt("Testo da rendere in grassetto:");
                  if (!txt) return;
                  insertContent((value) => `**${value}**`, txt);
                }}
                style={{ padding: "6px 12px", background: "#f3f4f6", border: "1px solid #ccc", borderRadius: 6, cursor: "pointer", fontSize: 13, fontWeight: 700 }}>
                B
              </button>
              <button type="button"
                onClick={() => {
                  const selected = selectedContentText();
                  const txt = selected || prompt("Testo in corsivo:");
                  if (!txt) return;
                  insertContent((value) => `*${value}*`, txt);
                }}
                style={{ padding: "6px 12px", background: "#f3f4f6", border: "1px solid #ccc", borderRadius: 6, cursor: "pointer", fontSize: 13, fontStyle: "italic" }}>
                I
              </button>
              <button type="button"
                onClick={() => {
                  const selected = selectedContentText();
                  const txt = selected || prompt("Citazione:");
                  if (!txt) return;
                  insertContent((value) => `\n\n> ${value}\n\n`, txt);
                }}
                style={{ padding: "6px 12px", background: "#f3f4f6", border: "1px solid #ccc", borderRadius: 6, cursor: "pointer", fontSize: 13 }}>
                ❝ Citazione
              </button>
              <small style={{ alignSelf: "center", color: "#666", marginLeft: 4 }}>
                Per la SEO: usa <strong>un solo H1</strong> (di norma il titolo), poi H2 per le sezioni e H3 per i sotto-paragrafi.
              </small>
            </div>
          </div>
          <button onClick={() => save(editing)} disabled={busy || !editing.title}
            style={{ padding: 12, background: "#2c6e8c", color: "#fff", border: 0, borderRadius: 6, fontSize: 14, cursor: "pointer" }}>
            {busy ? "Salvataggio…" : "Salva"}
          </button>
          {msg && <p style={{ color: "#c00", fontSize: 13 }}>{msg}</p>}
        </div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 900, margin: "40px auto", padding: 24, fontFamily: "system-ui" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 24, margin: 0 }}>Admin — Articoli</h1>
          <p style={{ fontSize: 13, color: "#666", margin: "4px 0 0" }}>{userEmail}</p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={() => setEditing({ ...empty })}
            style={{ padding: "8px 16px", background: "#2c6e8c", color: "#fff", border: 0, borderRadius: 6, fontSize: 14, cursor: "pointer" }}>
            + Nuovo
          </button>
          <button onClick={logout}
            style={{ padding: "8px 16px", background: "#fff", border: "1px solid #ccc", borderRadius: 6, fontSize: 14, cursor: "pointer" }}>
            Esci
          </button>
        </div>
      </div>
      {msg && <p style={{ color: "#c00", fontSize: 13, marginBottom: 12 }}>{msg}</p>}
      {loadingList ? (
        <p style={{ color: "#666" }}>Caricamento…</p>
      ) : articles.length === 0 ? (
        <p style={{ color: "#666", padding: 40, border: "1px dashed #ccc", borderRadius: 6, textAlign: "center" }}>
          Nessun articolo. Premi "Nuovo" per crearne uno.
          <br /><small>Se vedi questo messaggio anche con articoli esistenti, probabilmente il tuo utente non ha il ruolo editor/admin.</small>
        </p>
      ) : (
        <div style={{ border: "1px solid #eee", borderRadius: 6 }}>
          {articles.map((a) => (
            <div key={a.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: 12, borderBottom: "1px solid #eee", gap: 12 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ width: 8, height: 8, borderRadius: "50%", background: a.published ? "#2c6e8c" : "#ccc", display: "inline-block" }} />
                  <strong style={{ fontSize: 14 }}>{a.title}</strong>
                  <span style={{ fontSize: 10, background: "#f0f0f0", padding: "2px 6px", borderRadius: 4, textTransform: "uppercase" }}>{a.language}</span>
                </div>
                <p style={{ fontSize: 12, color: "#999", margin: "4px 0 0" }}>/{a.slug}</p>
              </div>
              <div style={{ display: "flex", gap: 4 }}>
                <button onClick={() => setEditing(a)}
                  style={{ padding: "6px 10px", background: "#fff", border: "1px solid #ccc", borderRadius: 6, fontSize: 13, cursor: "pointer" }}>
                  Modifica
                </button>
                <button onClick={() => remove(a.id)}
                  style={{ padding: "6px 10px", background: "#fff", border: "1px solid #c00", color: "#c00", borderRadius: 6, fontSize: 13, cursor: "pointer" }}>
                  Elimina
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
